import { notifyCustomerUpdated } from '../../../common/signalr/signalrActions';
import { createEpicFromAsyncAction, createEpicFromAsyncActionEmptyRequest } from '../../../utils';
import { enqueueSnackbar, pricePrgError } from '../../ui/redux/uiSlices';

import {
    fetchCustomers,
    fetchPricelistsForCustomer,
    sendPricelistToCustomer,
    sendPricelistToEmail,
    updateCustomer,
    fetchPricelistPeriodsForPricelists,
    createCustomer,
    deleteCustomer,
    showCreateCustomerDialog,
    showSendPricelistToEmailDialog,
    showSendPricelistToCustomerDialog,
    fetchPurchasers,
    customerSelected,
    fetchCustomer
} from './customerSlices';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { RootEpic } from '../../../store';

const fetchCustomers$ = createEpicFromAsyncActionEmptyRequest(fetchCustomers, ({ customerClient }) =>
    customerClient.getAll()
);

const updateCustomer$ = createEpicFromAsyncAction(
    updateCustomer,
    async (payload, { customerClient }) => {
        const { customerId, ...saveDto } = payload;
        await customerClient.update(customerId, saveDto);
        return payload;
    },
    req => notifyCustomerUpdated(req.customerId)
);

const sendPricelistToCustomer$ = createEpicFromAsyncAction(
    sendPricelistToCustomer,
    (payload, { customerClient }) =>
        customerClient.sendPricelistsToCustomer(
            payload.mailSendoutId,
            payload.customerId,
            payload.pricelistId,
            payload.validAt,
            payload.purchaser
        ),
    () =>
        enqueueSnackbar({
            message: 'Pricelist mail successfully sent!',
            severity: 'success'
        }),
    (_, failure) => pricePrgError(failure)
);

const sendPricelistToEmail$ = createEpicFromAsyncAction(
    sendPricelistToEmail,
    (payload, { customerClient }) =>
        customerClient.sendPricelistsToMail(
            payload.mailSendoutId,
            payload.email,
            payload.pricelistId,
            payload.customerId,
            payload.validAt,
            payload.purchaser
        ),
    () =>
        enqueueSnackbar({
            message: 'Pricelist mail successfully sent!',
            severity: 'success'
        }),
    (_, failure) => pricePrgError(failure)
);

const fetchPricelistsForCustomer$ = createEpicFromAsyncAction(
    fetchPricelistsForCustomer,
    (payload, { pricelistClient }) => pricelistClient.getPricelistsForCustomer(payload)
);

const fetchPricelistPeriodsForPricelists$ = createEpicFromAsyncAction(
    fetchPricelistPeriodsForPricelists,
    (payload, { pricelistClient }) => pricelistClient.getPricelistsPeriodsForPricelists(payload)
);

const createCustomer$ = createEpicFromAsyncAction(
    createCustomer,
    (payload, { customerClient }) => customerClient.create(payload),
    () => [fetchCustomers.request(), showCreateCustomerDialog(false)],
    (request, error) => {
        console.log(error);
        return enqueueSnackbar({
            message: `Couldn't create customer with customer number ${request.customerNumber}`,
            severity: 'error',
            autoHideDuration: 3000
        });
    }
);

const deleteCustomer$ = createEpicFromAsyncAction(
    deleteCustomer,
    (payload, { customerClient }) => customerClient.delete(payload),
    req => deleteCustomer.success(req)
);

const fetchPurchasersIfNeeded$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            action =>
                showSendPricelistToEmailDialog.match(action) ||
                showSendPricelistToCustomerDialog.match(action)
        ),
        withLatestFrom(state$.pipe(map(state => state.customers.purchasers))),
        filter(([, state$]) => state$.length === 0),
        map(() => fetchPurchasers.request())
    );

const fetchPurchasers$ = createEpicFromAsyncActionEmptyRequest(fetchPurchasers, ({ customerClient }) =>
    customerClient.getAvailablePurchasers()
);

const customerSelectedFetchData$: RootEpic = action$ =>
    action$.pipe(
        filter(customerSelected.match),
        filter(action => action.payload != null && action.payload > 0),
        mergeMap(action => [
            fetchCustomer.request(action.payload!),
            fetchPricelistsForCustomer.request(action.payload!)
        ])
    );

const fetchCustomer$ = createEpicFromAsyncAction(fetchCustomer, (customerId, { customerClient }) =>
    customerClient.get(customerId)
);

export default [
    fetchCustomers$,
    updateCustomer$,
    sendPricelistToCustomer$,
    sendPricelistToEmail$,
    fetchPricelistsForCustomer$,
    fetchPricelistPeriodsForPricelists$,
    createCustomer$,
    deleteCustomer$,
    fetchPurchasersIfNeeded$,
    fetchPurchasers$,
    fetchCustomer$,
    customerSelectedFetchData$
];
